import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";
import { trackVisit } from "@/lib/analytics";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import GoogleCallback from "@/pages/GoogleCallback";
import DashboardHome from "@/pages/DashboardHome";
import ProjectsPage from "@/pages/ProjectsPage";
import ProjectDetail from "@/pages/ProjectDetail";
import PreviewPage from "@/pages/PreviewPage";
import TeamPage from "@/pages/TeamPage";
import AcceptInvite from "@/pages/AcceptInvite";
import PlatformAdmin from "@/pages/PlatformAdmin";
import Billing from "@/pages/Billing";
import SupportPage from "@/pages/SupportPage";
import Privacy from "@/pages/Privacy";
import Contact from "@/pages/Contact";
import Docs from "@/pages/Docs";
import Terms from "@/pages/Terms";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AppLayout from "@/components/AppLayout";
import PublicSubmission from "@/pages/PublicSubmission";

function FullLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="h-6 w-6 animate-spin text-primary" />
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <FullLoader />;
  if (user === null) return <Navigate to="/login" replace />;
  return children;
}

function GuestRoute({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <FullLoader />;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function StaffRoute({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <FullLoader />;
  if (user === null) return <Navigate to="/login" replace />;
  if (!(user.role === "admin" && user.is_staff)) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  useEffect(() => {
    const publicPaths = ["/", "/privacy", "/terms", "/contact", "/docs"];
    if (publicPaths.includes(location.pathname)) {
      trackVisit(location.pathname);
    }
  }, [location.pathname]);
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/docs" element={<Docs />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/login" element={<GuestRoute><Login /></GuestRoute>} />
      <Route path="/auth/google" element={<GoogleCallback />} />
      <Route path="/register" element={<GuestRoute><Register /></GuestRoute>} />
      <Route path="/preview/:projectKey" element={<PreviewPage />} />
      <Route path="/s/:token" element={<PublicSubmission />} />
      <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        <Route path="/dashboard" element={<DashboardHome />} />
        <Route path="/projects" element={<ProjectsPage />} />
        <Route path="/projects/:id" element={<ProjectDetail />} />
        <Route path="/team" element={<TeamPage />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/support" element={<SupportPage />} />
      </Route>
      <Route path="/invite/:token" element={<ProtectedRoute><AcceptInvite /></ProtectedRoute>} />
      <Route path="/platform" element={<StaffRoute><PlatformAdmin /></StaffRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}
